/**
 * Dash0 RUM without the SDK in the page's initial JavaScript, where its chunk
 * would compete with the LCP image for bandwidth: entry.client.tsx loads it
 * alongside hydration. Calls and uncaught errors made meanwhile are held here
 * and replayed, in order, after init; LCP, CLS and the first input reach the
 * SDK regardless, through buffered performance observers.
 *
 * What a late init loses, and why the chunk starts loading as early as it
 * does: fetches before it get no span or traceparent, interactions under
 * 104 ms before it miss INP, and a visit that ends before it sends nothing.
 * Pages that fetch while they hydrate therefore bring the SDK along
 * (~/lib/rum-preload) and start RUM before hydrateRoot.
 */
import type * as Dash0 from '@dash0/sdk-web';

type Sdk = Pick<typeof Dash0, 'init' | 'addSignalAttribute' | 'reportError'>;
type InitOptions = Parameters<Sdk['init']>[0];
type Call = (sdk: Sdk) => void;

// The SDK stops after 100 errors a page anyway; past that, an error thrown in
// a loop while the chunk loads would only grow the queue.
const MAX_QUEUED_ERRORS = 100;

let sdk: Sdk | undefined;
let preloaded: Sdk | undefined;
// Left undefined wherever RUM never starts — the server, or a client without
// an endpoint configured — so calls there are dropped rather than piling up.
let pending: { calls: Call[]; errors: number } | undefined;

export function reportError(...args: Parameters<Sdk['reportError']>): void {
  if (sdk) {
    sdk.reportError(...args);
  } else if (pending && pending.errors < MAX_QUEUED_ERRORS) {
    pending.errors++;
    pending.calls.push((loaded) => loaded.reportError(...args));
  }
}

export function addSignalAttribute(
  ...args: Parameters<Sdk['addSignalAttribute']>
): void {
  if (sdk) sdk.addSignalAttribute(...args);
  else pending?.calls.push((loaded) => loaded.addSignalAttribute(...args));
}

/** For ~/lib/rum-preload: the SDK this page loaded with its own JavaScript. */
export function preloadedSdk(loaded: Sdk): void {
  preloaded ??= loaded;
}

/**
 * Initializes synchronously when the page preloaded the SDK, and never rejects:
 * a page without RUM must still work.
 */
export async function startRum(
  load: () => Promise<Sdk>,
  options: InitOptions
): Promise<void> {
  const queue: Call[] = [];
  pending = { calls: queue, errors: 0 };
  window.addEventListener('error', bufferUncaughtError);
  window.addEventListener('unhandledrejection', bufferUnhandledRejection);

  let loaded: Sdk;
  try {
    loaded = preloaded ?? (await load());
    loaded.init(options);
  } catch (error) {
    console.warn('Dash0 RUM did not start', error);
    return;
  } finally {
    // From init on, the SDK's own window handlers report uncaught errors, so
    // buffering past it would report each of them twice. If the chunk never
    // loads (offline, blocked) there is nothing to replay into.
    window.removeEventListener('error', bufferUncaughtError);
    window.removeEventListener('unhandledrejection', bufferUnhandledRejection);
    pending = undefined;
  }

  sdk = loaded;
  for (const replay of queue) {
    // One call the SDK throws on must not cost the ones queued after it.
    try {
      replay(loaded);
    } catch (error) {
      console.warn('Dash0 RUM call failed', error);
    }
  }
}

// The two handlers below mirror what the SDK's own handlers send, so an error
// thrown while it loads reads the same in Dash0 as one thrown after.

function bufferUncaughtError({
  message,
  filename,
  lineno,
  colno,
  error,
}: ErrorEvent): void {
  const stack: string = error?.stack || `at ${filename} ${lineno}:${colno}`;
  reportError({ message: String(message), stack });
}

const REJECTION_PREFIX = 'Unhandled promise rejection: ';
const NO_STACK =
  "<unavailable because Promise wasn't rejected with an Error object>";

function bufferUnhandledRejection({ reason }: PromiseRejectionEvent): void {
  if (reason == null) {
    reportError({
      message: `${REJECTION_PREFIX}<no reason defined>`,
      stack: NO_STACK,
    });
  } else if (typeof reason.message === 'string') {
    reportError({
      message: REJECTION_PREFIX + reason.message,
      stack: typeof reason.stack === 'string' ? reason.stack : NO_STACK,
    });
  } else if (typeof reason !== 'object') {
    reportError({
      message: REJECTION_PREFIX + String(reason),
      stack: NO_STACK,
    });
  }
}
